import { useState } from "react";
import { useAnalyticsResultDetail } from "../../../../hooks/audit/useAnalyticsResultDetail";
import { useOverrideAuthorName } from "../../../../hooks/audit/useOverrideAuthorName";
import { verdictMethodOf } from "../../../../models/audit/analyticsColumnResult";
import { analyticsConclusionProse } from "../../../../models/audit/analyticsTranslator";
import {
  concludedByLine,
  type VerdictConclusionFields,
} from "../lib/verdictOrigin";
import { ConclusionHoverCard } from "./ConclusionHoverCard";

interface AnalyticsConclusionCardProps {
  /** Procedure-session id used to scope the lazy detail fetch/cache key. */
  procedureSessionId: string;
  /** Bare analytics-verdict id (no `analytics-id-` prefix). */
  analyticsResultId: string;
  /**
   * The verdict's provenance, off the summary the cell already holds, which
   * answers until the detail fetch below settles, so the card opens saying
   * whether Midship or an auditor concluded the verdict without waiting on the
   * detail fetch. Another auditor's name follows the user lookup that starts on
   * open.
   */
  provenance: VerdictConclusionFields;
}

/**
 * The conclusion affordance on an analytics cell, and the only element on the
 * grid that can say who concluded the verdict.
 *
 * It says one thing more than the attribute-test card: the "Concluded by" line
 * the review pane shows under the conclusion. `getStatusClasses` and
 * `getStatusDisplay` deliberately know nothing of column kind — their four
 * colours and four labels are shared by the support, evaluated and
 * attribute-test cells, and a `fail` that looked different because of which
 * engine produced it would make the colour ambiguous on every kind while
 * encoding a fact the column header already states. So the kind's one extra
 * fact lands here instead, beside the badge rather than in it.
 *
 * The judge and run details stay on the review pane's line rather than nesting
 * a tooltip inside this hover card.
 *
 * The heavy conclusion is fetched lazily on first open. The line is not: until
 * that fetch settles it is derived from the summary the row already carried.
 * Once the full verdict arrives the line is read from that instead, so the
 * card and the review pane name the same author from the same record. The
 * overriding auditor is looked up only once the card has opened.
 */
export const AnalyticsConclusionCard = ({
  procedureSessionId,
  analyticsResultId,
  provenance,
}: AnalyticsConclusionCardProps) => {
  // Keep the fetch enabled after the first open so re-opening reads from cache
  // instantly instead of re-triggering the request.
  const [hasOpened, setHasOpened] = useState(false);

  const { data: result, isLoading } = useAnalyticsResultDetail(
    procedureSessionId,
    hasOpened ? analyticsResultId : undefined,
  );

  const fields: VerdictConclusionFields = result ?? provenance;
  const method = verdictMethodOf(fields);
  const auditorName = useOverrideAuthorName(
    hasOpened && method === "human_override" ? fields.userId : null,
  );

  const conclusion = result ? analyticsConclusionProse(result) : null;

  return (
    <ConclusionHoverCard
      onOpen={() => setHasOpened(true)}
      isLoading={isLoading}
      conclusion={conclusion}
      emptyMessage="No conclusion available for this verdict."
      footnote={concludedByLine(method, auditorName)}
    />
  );
};
